import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building, User, MapPin, Phone, Mail, Clock, CreditCard, Truck, RefreshCw, Tag, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CommercialDisclosure() {
  const { t, i18n } = useTranslation();
  const [disclosure, setDisclosure] = useState(null);
  const [loading, setLoading] = useState(true);
  const isJapanese = i18n.language === 'ja';

  useEffect(() => {
    const fetchDisclosure = async () => {
      try {
        const response = await fetch(`${API_URL}/api/commercial-disclosure`);
        if (response.ok) {
          const data = await response.json();
          setDisclosure(data);
        }
      } catch (error) {
        console.error('Error fetching commercial disclosure:', error);
      }
      setLoading(false);
    };
    fetchDisclosure();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const sections = [
    { 
      icon: Building, 
      title: isJapanese ? '販売業者' : 'Legal Name', 
      content: isJapanese ? disclosure?.legal_name_japanese || disclosure?.legal_name : disclosure?.legal_name,
      show: disclosure?.legal_name
    },
    { 
      icon: User, 
      title: isJapanese ? '運営統括責任者' : 'Head of Operations', 
      content: disclosure?.representative,
      show: disclosure?.representative
    },
    { 
      icon: MapPin, 
      title: isJapanese ? '所在地' : 'Address', 
      content: isJapanese ? disclosure?.address_japanese || disclosure?.address : disclosure?.address,
      show: disclosure?.address
    },
    { 
      icon: Phone, 
      title: isJapanese ? '電話番号' : 'Phone Number', 
      content: disclosure?.phone,
      show: disclosure?.phone
    },
    { 
      icon: Mail, 
      title: isJapanese ? 'メールアドレス' : 'Email Address', 
      content: disclosure?.email,
      show: disclosure?.email
    },
    { 
      icon: Clock, 
      title: isJapanese ? '営業時間' : 'Business Hours', 
      content: disclosure?.business_hours,
      show: disclosure?.business_hours
    },
    { 
      icon: Tag, 
      title: isJapanese ? '商品以外の必要代金' : 'Additional Fees', 
      content: disclosure?.additional_fees,
      show: disclosure?.additional_fees
    },
    { 
      icon: CreditCard, 
      title: isJapanese ? 'お支払い方法' : 'Payment Methods', 
      content: disclosure?.payment_methods,
      show: disclosure?.payment_methods
    },
    { 
      icon: CreditCard, 
      title: isJapanese ? 'お支払い期限' : 'Payment Period', 
      content: disclosure?.payment_period,
      show: disclosure?.payment_period
    },
    { 
      icon: Truck, 
      title: isJapanese ? '引き渡し時期' : 'Delivery Times', 
      content: disclosure?.delivery_times,
      show: disclosure?.delivery_times
    },
    { 
      icon: Truck, 
      title: isJapanese ? '配送について' : 'Shipping Information', 
      content: disclosure?.shipping_info,
      show: disclosure?.shipping_info
    },
    { 
      icon: RefreshCw, 
      title: isJapanese ? '返品・交換について' : 'Returns & Exchanges Policy', 
      content: disclosure?.returns_policy,
      show: disclosure?.returns_policy
    },
    { 
      icon: Tag, 
      title: isJapanese ? '販売価格' : 'Price', 
      content: disclosure?.price_info,
      show: disclosure?.price_info
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="py-6 px-4 border-b border-gray-800">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{isJapanese ? 'ホームに戻る' : 'Back to Home'}</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-cyan-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {isJapanese ? '特定商取引法に基づく表記' : 'Commercial Disclosure'}
          </h1>
          <p className="text-gray-400">
            {isJapanese 
              ? 'Specified Commercial Transactions Act' 
              : "Based on Japan's Specified Commercial Transactions Act"}
          </p>
        </div>

        {/* Disclosure Sections */}
        <div className="space-y-6">
          {sections.filter(s => s.show).map((section, index) => (
            <div 
              key={index}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-cyan-500/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <section.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Sections */}
          {disclosure?.custom_sections?.map((section, index) => (
            <div 
              key={`custom-${index}`}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-cyan-500/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-purple-400 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Content Message */}
        {!disclosure?.legal_name && (
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              {isJapanese 
                ? '商取引情報はまだ設定されていません。' 
                : 'Commercial disclosure information has not been set up yet.'}
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800 mt-12">
        <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm">
          <Link to="/" className="hover:text-cyan-400 transition-colors">
            © {new Date().getFullYear()} Okinawa Sign Studio
          </Link>
        </div>
      </footer>
    </div>
  );
}
